import { Item, ItemProperties } from './Item';
import { ReactNode } from 'react';
import { Field } from './Field';

/**
 * Link properties
 */
export interface LinkProperties extends ItemProperties {
  enabled: boolean;
}

/**
 * Link types
 */
export type LinkType = 'beat' | 'video' | 'audio';

/**
 * Action types
 */
export type ActionType = 'minmax' | 'toggle';

/**
 * Easing functions
 */
export type EasingType = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut';

/**
 * Curve types
 */
export type CurveType = 'linear' | 'smooth';

/**
 * Base interface for all actions
 */
export interface Action {
  id: string;
  name: string;
  type: ActionType;
  enabled: boolean;
  targetItemId: string;
  targetProperty: string;
  easing: EasingType;
  curve: CurveType;
  restValue?: any;
  
  // MinMax specific properties
  range?: {
    min: number;
    max: number;
  };
  
  // Toggle specific properties
  defaultState?: boolean;
}

/**
 * Link settings based on type
 */
export interface LinkSettings {
  // Beat settings
  triggerBeats?: number[];
  duration?: number;
  
  // Video settings
  videoSource?: 'camera1' | 'camera2' | 'camera3';
  trackingTarget?: 'person1' | 'person2' | 'person3';
  
  // Audio settings
  audioSource?: 'microphone1' | 'microphone2' | 'systemAudio';
  analysisType?: 'amplitude' | 'frequency';
  threshold?: number;
}

/**
 * Unified Link interface
 */
export interface Link extends Item {
  type: 'link';
  linkType: LinkType;
  actions: Action[];
  properties: LinkProperties;
  settings: LinkSettings;
}

/**
 * Create a new action
 */
export function createAction(type: ActionType, name: string): Action {
  const baseAction: Action = {
    id: crypto.randomUUID(),
    name,
    type,
    enabled: true,
    targetItemId: '',
    targetProperty: '',
    easing: 'linear',
    curve: 'linear'
  };

  if (type === 'minmax') {
    return {
      ...baseAction,
      range: {
        min: 0,
        max: 1
      }
    };
  } else {
    return {
      ...baseAction,
      defaultState: false
    };
  }
}

/**
 * Create a new link
 */
export function createLink(linkType: LinkType, name: string = 'New Link'): Link {
  const baseLink: Link = {
    id: crypto.randomUUID(),
    name,
    type: 'link',
    linkType,
    actions: [],
    properties: {
      enabled: true,
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
    },
    settings: {},
    fields: [],
    render: () => null
  };

  switch (linkType) {
    case 'beat':
      return {
        ...baseLink,
        settings: {
          triggerBeats: [1],
          duration: 1
        }
      };
    case 'video':
      return {
        ...baseLink,
        settings: {
          videoSource: 'camera1',
          trackingTarget: 'person1'
        }
      };
    case 'audio':
      return {
        ...baseLink,
        settings: {
          audioSource: 'microphone1',
          analysisType: 'amplitude',
          threshold: 0.5
        }
      };
    default:
      return {
        ...baseLink,
        settings: {
          triggerBeats: [1],
          duration: 1
        }
      };
  }
}

/**
 * Helper functions for UI fields
 */
export const linkFields = {
  common: [
    {
      key: 'name',
      type: 'string' as const,
      label: 'Name',
      default: '',
      panel: 'transform' as const,
    },
    {
      key: 'linkType',
      type: 'button-group' as const, 
      label: 'Link Type',
      default: 'beat',
      panel: 'transform' as const,
      options: [
        { label: 'Beat', value: 'beat' },
        { label: 'Video', value: 'video' },
        { label: 'Audio', value: 'audio' },
      ],
    },
    {
      key: 'properties.enabled',
      type: 'boolean' as const,
      label: 'Enabled',
      default: true,
      panel: 'transform' as const,
    }
  ] as Field[],
  
  beat: [
    {
      key: 'settings.triggerBeats',
      type: 'select' as const,
      label: 'Trigger Beats',
      default: '1',
      panel: 'transform' as const,
      options: [], // Will be populated dynamically based on time signature
    },
    {
      key: 'settings.duration',
      type: 'number' as const,
      label: 'Duration (beats)',
      default: 1,
      min: 1,
      max: 16,
      panel: 'transform' as const,
    }
  ] as Field[],
  
  video: [
    {
      key: 'settings.videoSource',
      type: 'select' as const,
      label: 'Video Source',
      default: 'camera1',
      panel: 'transform' as const,
      options: [
        { label: 'Camera 1', value: 'camera1' },
        { label: 'Camera 2', value: 'camera2' },
        { label: 'Camera 3', value: 'camera3' },
      ],
    },
    {
      key: 'settings.trackingTarget',
      type: 'select' as const,
      label: 'Tracking Target',
      default: 'person1',
      panel: 'transform' as const,
      options: [
        { label: '1 Person', value: 'person1' },
        { label: '2 Persons', value: 'person2' },
        { label: '3 Persons', value: 'person3' },
      ],
    }
  ] as Field[],
  
  audio: [
    {
      key: 'settings.audioSource',
      type: 'select' as const,
      label: 'Audio Source',
      default: 'microphone1',
      panel: 'transform' as const,
      options: [
        { label: 'Microphone 1', value: 'microphone1' },
        { label: 'Microphone 2', value: 'microphone2' },
        { label: 'System Audio', value: 'systemAudio' },
      ],
    },
    {
      key: 'settings.analysisType',
      type: 'button-group' as const,
      label: 'Analysis Type',
      default: 'amplitude',
      panel: 'transform' as const,
      options: [
        { label: 'Amplitude', value: 'amplitude' },
        { label: 'Frequency', value: 'frequency' },
      ],
    },
    {
      key: 'settings.threshold',
      type: 'number' as const,
      label: 'Threshold',
      default: 0.5,
      min: 0,
      max: 1,
      panel: 'transform' as const,
    }
  ] as Field[]
};